// Adapted from the @shadcndesign app-shell-4 TeamSwitcher block.
import React, { useId, useRef } from "react";
import { ChevronsUpDown } from "lucide-react";

import ClubLogo from "@/Components/ClubLogo";
import { useTranslation } from "@/context/Internationalization";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/shadcn/components/ui/sidebar";

const ClubIdentity = ({ club, season, seasonLabel, textId }) => (
  <>
    {/* The club name is spelled out next to the logo. */}
    <span aria-hidden="true" className="flex">
      <ClubLogo club={club} />
    </span>
    <div id={textId} className="grid flex-1 text-left text-sm leading-tight">
      <span className="truncate font-semibold" title={club.name}>
        {club.name}
      </span>
      <span className="truncate text-xs" title={`${seasonLabel}: ${season}`}>
        {season}
      </span>
    </div>
  </>
);

const ClubSwitcher = ({
  club,
  clubs = [],
  selectedClubId,
  season,
  onClubChange,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  const labelId = useId();
  const identityId = useId();
  // Report the pick after the menu has closed and returned focus to its
  // trigger, so the confirmation dialog restores focus there afterwards.
  const pendingClubId = useRef(null);

  if (!club?._id) {
    return (
      <div className="p-2">
        <ClubLogo />
      </div>
    );
  }

  const value = selectedClubId || club._id;
  const displayedClub =
    value === club._id
      ? club
      : clubs.find((option) => option._id === value) || club;
  const identity = (
    <ClubIdentity
      club={displayedClub}
      season={season}
      seasonLabel={t("season")}
      textId={identityId}
    />
  );

  if (clubs.length <= 1) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <div className="flex h-12 items-center gap-2 px-3 text-sm group-data-[collapsible=icon]:px-0">
            {identity}
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu
          onOpenChangeComplete={(open) => {
            if (open || !pendingClubId.current) return;
            const clubId = pendingClubId.current;
            pendingClubId.current = null;
            onClubChange(clubId);
          }}
        >
          {/* The spoken name includes the visible club name (WCAG 2.5.3). */}
          <DropdownMenuTrigger
            aria-labelledby={`${labelId} ${identityId}`}
            render={
              <SidebarMenuButton
                size="lg"
                className="data-popup-open:bg-sidebar-accent data-popup-open:text-sidebar-accent-foreground"
              />
            }
          >
            <span id={labelId} className="sr-only">
              {t("club")}
            </span>
            {identity}
            <ChevronsUpDown className="ml-auto" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--anchor-width) min-w-56"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuRadioGroup
              value={value}
              onValueChange={(clubId) => {
                if (clubId !== value) pendingClubId.current = clubId;
              }}
            >
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t("club")}
              </DropdownMenuLabel>
              {clubs.map((option) => (
                <DropdownMenuRadioItem
                  key={option._id}
                  value={option._id}
                  closeOnClick
                >
                  <span aria-hidden="true" className="flex">
                    <ClubLogo club={option} size={24} />
                  </span>
                  <span className="truncate">{option.name}</span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

export default ClubSwitcher;
